import { useGetApiListNoToast, useMutateGetApi } from '@/apiError/apiShow';
import { useUpdateApiNoToast } from '@/apiError/apiUpdate';
import { AntButton } from '@/common/ant-components/AntButton';
import { useTransactionContext } from '@/context/TransactionContext';
import { PrintFormatRenderer } from '@/features/format-designer/PrintFormatRenderer';
import { getPrintFormatFromTemplate } from '@/utils/transaction/jewelry-new-sticker/buildBillTemplatePayload';
import { BillPrintMapper } from '@/utils/transaction/jewelry-new-sticker/mapper/bill';
import {
  mapAppToBusiness,
  mapRawSummaryToTokenContext,
} from '@/shared/utils/invoiceMapper';
import { downloadNodeAsPdf } from '@/utils/pdf/downloadNodeAsPdf';
import { showToast } from '@/utils/toastMessage/ToastMessage';
import useAppData from '@/hooks/useAppData';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useReactToPrint } from 'react-to-print';
import { FaFilePdf, FaPrint } from 'react-icons/fa6';

const PrintPlayGround = () => {
  const previewRef = useRef(null);
  const printRef = useRef(null);
  const navigate = useNavigate();
  const { app } = useAppData();
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  const {
    billTemplate,
    setBillTemplate,
    billTemplateCustom,
    setBillTemplateCustom,
    billPrintUrls,
    setBillPrintUrls,
    billTemplateDataCustom,
    setBillTemplateDataCustom,
  } = useTransactionContext();

  const {
    mutateAsync: mutateEditPreviewApi,
    // isPending: loadingPrinter,
    data: previewData,
  } = useMutateGetApi();

  const {
    mutateAsync: mutateEditPrintApi,
    // isPending: loadingPrinter,
    data: printData,
  } = useMutateGetApi();

  const { mutate: incrementPrint } = useUpdateApiNoToast();

  const { data: billTemplateData } = useGetApiListNoToast(
    billPrintUrls?.BILL_SETUP_URL,
    {
      params: {
        q: '',
      },
    },
  );

  const handleOnBeforeGetContent = useCallback(() => {
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve();
      }, 10);
    });
  }, []);

  const handleBeforePrint = useCallback(() => {});

  const renderBill = (tpl, data, ref, hidden) => {
    const printFormat = getPrintFormatFromTemplate(
      tpl?.editorConfig,
      tpl?.configuration,
    );

    let node;
    if (printFormat) {
      node = (
        <PrintFormatRenderer
          ref={ref}
          printFormat={printFormat}
          tokenCtx={mapRawSummaryToTokenContext(
            data?.summaryData,
            data?.detailsData,
            mapAppToBusiness(app),
            data?.goldRateInTola,
            data?.silverRateInTola,
          )}
        />
      );
    } else if (tpl?.editorConfig && tpl?.contentBlocks?.length) {
      node = (
        <BillPrintMapper
          ref={ref}
          data={data}
          blocks={tpl.contentBlocks}
          editorConfig={tpl.editorConfig}
          copies={[]}
          isHidden={false}
        />
      );
    } else {
      return null;
    }

    return hidden ? (
      <div style={{ position: 'fixed', left: '-9999px', top: 0 }}>{node}</div>
    ) : (
      node
    );
  };

  const handleDownloadPdf = async () => {
    setDownloadingPdf(true);
    try {
      const billNumber = previewData?.data?.summaryData?.billNumber;
      await downloadNodeAsPdf(
        previewRef.current,
        billNumber ? `bill-${billNumber}` : 'bill',
      );
    } catch (error) {
      showToast(error?.message || 'Could not generate the PDF.', 'error');
    } finally {
      setDownloadingPdf(false);
    }
  };

  const printTemplate = useReactToPrint({
    content: () => printRef.current,
    documentTitle: '\u00A0',
    onBeforeGetContent: handleOnBeforeGetContent,
    onBeforePrint: handleBeforePrint,
    removeAfterPrint: true,
  });
  useEffect(() => {
    const fetchData = async () => {
      if (billTemplate || setBillTemplateCustom) {
        await mutateEditPreviewApi({
          url: `${billPrintUrls?.DATA_URL}&preview=true`,
        });
      }
    };
    fetchData();
  }, [billPrintUrls]);

  return (
    <div className="tw-flex tw-justify-center tw-items-center tw-flex-col tw-w-full tw-h-full tw-bg-gray-100 tw-text-center">
      {downloadingPdf && (
        <div className="tw-fixed tw-inset-0 tw-z-[9999] tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-3 tw-bg-black/50">
          <div className="tw-h-10 tw-w-10 tw-animate-spin tw-rounded-full tw-border-4 tw-border-solid tw-border-white/30 tw-border-t-white" />
          <span className="tw-text-base tw-tracking-widest tw-text-white">
            Downloading...
          </span>
        </div>
      )}
      <div className="tw-flex tw-items-center tw-justify-between tw-w-full tw-px-16 tw-pt-3">
        <AntButton
          className="tw-px-1 tw-py-1 tw-rounded-lg tw-w-28 tw-text-base tw-tracking-widest tw-font-medium"
          onClick={() => {
            const returnUrl = billPrintUrls?.RETURN_URL || '/sales/list/';
            setBillTemplate(null);
            setBillTemplateCustom(null);
            setBillPrintUrls(null);
            navigate(returnUrl);
          }}
        >
          Back
        </AntButton>
        <div className="tw-flex tw-items-center tw-gap-2">
          <AntButton
            type="primary"
            size="large"
            onClick={handleDownloadPdf}
            className="tw-px-4 tw-py-1 tw-my-1 tw-rounded-xl tw-w-28 tw-text-base tw-tracking-widest tw-font-medium"
          >
            <FaFilePdf />
            Pdf
          </AntButton>
          <AntButton
            type="primary"
            size="large"
            onClick={async () => {
              await mutateEditPrintApi({
                url: `${billPrintUrls?.DATA_URL}&preview=false`,
              });
              printTemplate();
            }}
            className="tw-px-4 tw-py-1 tw-my-1 tw-rounded-xl tw-w-28 tw-text-base tw-tracking-widest tw-font-medium"
          >
            <FaPrint />
            Print
          </AntButton>
        </div>
      </div>
      {billTemplate &&
        renderBill(
          billTemplateData?.data?.[0],
          previewData?.data,
          previewRef,
          false,
        )}
      {billTemplate &&
        renderBill(
          billTemplateData?.data?.[0],
          printData?.data,
          printRef,
          true,
        )}

      {billTemplateCustom &&
        billTemplateDataCustom &&
        renderBill(
          billTemplateDataCustom,
          previewData?.data,
          previewRef,
          false,
        )}
      {billTemplateCustom &&
        billTemplateDataCustom &&
        renderBill(billTemplateDataCustom, printData?.data, printRef, true)}
    </div>
  );
};

export default PrintPlayGround;
